/* eslint-disable no-unused-expressions */
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import baseURL from '../modules/baseURL';

const baseQueryWithToken = fetchBaseQuery({
  baseUrl: baseURL,
  prepareHeaders: (headers) => {
    let token;

    // Try multiple token storage locations for compatibility
    try {
      const ansData = localStorage.getItem('ans-sms');
      if (ansData) {
        const parsed = JSON.parse(ansData);
        token = parsed.token || parsed.jwt || parsed.accessToken;
      }
    } catch (e) {
      // Fallback to direct token keys
      token = localStorage.getItem('ans-sms.token')
              || localStorage.getItem('ans-sms.jwt')
              || localStorage.getItem('ans-sms.accessToken')
              || localStorage.getItem('token');
    }

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
});
export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithToken,
  endpoints: (builder) => ({
    getLessons: builder.query({
      query: () => '/lessons',
    }),
    getLessonById: builder.query({
      query: (lessonId) => `/lesson/${lessonId}`,
    }),
    getPublications: builder.query({
      query: () => '/publications',
    }),
    getSafetyLogs: builder.query({
      query: () => '/safety-logs/all',
    }),
    getOccurrences: builder.query({
      query: () => '/occurrences/all',
    }),
    getOccurrenceById: builder.query({
      query: (id) => `/occurrences/${id}`,
    }),
    // SRM Data endpoints
    getIncidentSubCauses: builder.query({
      query: () => '/occurrences-data/incident-sub-causes',
    }),
    getArticles: builder.query({
      query: () => '/all-articles',
    }),
    getAllNewsletterSubcribers: builder.query({
      query: () => '/newsletter/getAllSubscribers',
    }),
    getArticle: builder.query({
      query: (id) => `/articles/${id}`,
    }),
    getNewsletterdetails: builder.query({
      query: (id) => `/newsletter/get-newsletter/${id}`,
      transformResponse: (response) => response,
    }),
    getPublication: builder.query({
      query: (id) => `/publications/${id}`,
    }),
    getRecipients: builder.query({
      query: () => '/recipients',
    }),
    getNewsLetter: builder.query({
      query: () => '/newsletter/get-all-newsletters',
    }),
    addNewsLetterEmails: builder.mutation({
      query: (email) => ({
        url: '/newsletter',
        method: 'POST',
        body: (email),
      }),
    }),
    sendNewsletterEmail: builder.mutation({
      query: (newsletterId) => ({
        url: `/newsletter/sendNewsletters?newsletterId=${newsletterId}`,
        method: 'POST',
      }),
    }),
    addOccurrence: builder.mutation({
      query: (occObj) => ({
        url: '/occurrences/add',
        method: 'POST',
        body: occObj,
      }),
    }),
    uploadFile: builder.mutation({
      query: (formData) => ({
        url: '/publications',
        method: 'POST',
        body: formData,
      }),
    }),

    createPublication: builder.mutation({
      query: (publicationData) => ({
        url: '/add-publication',
        method: 'POST',
        body: publicationData,
      }),
    }),
    createArticle: builder.mutation({
      query: (articleData) => ({
        url: '/addArticle',
        method: 'POST',
        body: articleData,
      }),
    }),
    createNewsLetter: builder.mutation({
      query: (newsLetterData) => ({
        url: '/newsletter/add-newsletter',
        method: 'POST',
        body: newsLetterData,
      }),
    }),
    loginUser: builder.mutation({
      query: (userObj) => ({
        url: '/user/login',
        method: 'POST',
        body: userObj,
      }),
    }),
    addNewsletterSubscriber: builder.mutation({
      query: (subscriberObj) => ({
        url: '/newsletter/add-subscriber',
        method: 'POST',
        body: subscriberObj,
      }),
    }),
    unsubscribe: builder.mutation({
      query: ({ email, token }) => ({
        url: `/newsletter/remove-subscriber/${email}/${token}`,
        method: 'DELETE',
      }),
    }),
    getUsers: builder.query({
      query: (keyword) => `/user/getAllUsers?keyword=${keyword}`,
      transformResponse:
        (response, queryArgs) => {
          const requestUrl = queryArgs.request.url;
          const keyword = new URL(requestUrl).searchParams.get('keyword');
          return {
            users: response.filter((user) => user.name.toLowerCase().includes(keyword.toLowerCase())
            || user.email.toLowerCase().includes(keyword.toLowerCase())
            || user.department.toLowerCase().includes(keyword.toLowerCase())
            || user.position.toLowerCase().includes(keyword.toLowerCase())),
          };
        },
    }),
    getUser: builder.query({
      query: (id) => `/user/getUser/${id}`,
    }),
    deleteUser: builder.mutation({
      query: (userId) => ({
        url: `/user/deleteUser/${userId}`,
        method: 'DELETE',
      }),
    }),
    submitUser: builder.mutation({
      query: (user) => ({
        url: '/submituser',
        method: 'POST',
        body: user,
      }),
    }),
    deleteArticle: builder.mutation({
      query: (articleId) => ({
        url: `/deleteArticle/${articleId}`,
        method: 'DELETE',
      }),
    }),
    deletePublication: builder.mutation({
      query: (publicationId) => ({
        url: `/deletePublication/${publicationId}`,
        method: 'DELETE',
      }),
    }),
    deleteNewsletter: builder.mutation({
      query: (newsletterId) => ({
        url: `/newsletter/delete-newsletter/${newsletterId}`,
        method: 'DELETE',
      }),
    }),
    editArticle: builder.mutation({
      query: ({ articleId, updates }) => ({
        url: `editArticle/${articleId}`,
        method: 'PATCH',
        body: updates,
      }),
    }),
    editPublication: builder.mutation({
      query: ({ publicationId, updates }) => ({
        url: `editPublication/${publicationId}`,
        method: 'PATCH',
        body: updates,
      }),
    }),
    editNewsletter: builder.mutation({
      query: ({ newsletterId, updates }) => ({
        url: `/newsletter/edit-newsletter/${newsletterId}`,
        method: 'PATCH',
        body: updates,
      }),
    }),
    createLesson: builder.mutation({
      query: (lessonData) => ({
        url: '/add-lesson',
        method: 'POST',
        body: lessonData,
      }),
    }),
    deleteLesson: builder.mutation({
      query: (lessonId) => ({
        url: `/delete-lesson/${lessonId}`,
        method: 'DELETE',
      }),
    }),
    editLesson: builder.mutation({
      query: ({ lessonId, updates }) => ({
        url: `/edit-lesson/${lessonId}`,
        method: 'PATCH',
        body: updates,
      }),
    }),
    // SRM Data mutations
    addActionsTaken: builder.mutation({
      query: (actionsData) => ({
        url: '/occurrences-data/actions-taken/',
        method: 'POST',
        body: actionsData,
      }),
    }),
    updatePreliminaryActions: builder.mutation({
      query: ({ incidentId, updates }) => ({
        url: `/occurrences-data/${incidentId}/update-preliminary-actions`,
        method: 'PATCH',
        body: updates,
      }),
    }),
    addIncidentCauses: builder.mutation({
      query: (causeData) => ({
        url: '/occurrences-data/add-incident-causes/',
        method: 'POST',
        body: causeData,
      }),
    }),
    updateIncidentCause: builder.mutation({
      query: (updateData) => ({
        url: '/occurrences-data/update-incident-cause',
        method: 'PATCH',
        body: updateData,
      }),
    }),
    addSafetyRequirements: builder.mutation({
      query: ({ issueId, safetyData }) => ({
        url: `/occurrences-data/add-safety-requirements/${issueId}`,
        method: 'POST',
        body: safetyData,
      }),
    }),
    updateSafetyRequirements: builder.mutation({
      query: ({ safetyId, updates }) => ({
        url: `/occurrences-data/${safetyId}/update-safety-requirements`,
        method: 'PATCH',
        body: updates,
      }),
    }),
    updateSystemState: builder.mutation({
      query: ({ systemStateId, updates }) => ({
        url: `/occurrences-data/${systemStateId}/update-system-state-data`,
        method: 'PATCH',
        body: updates,
      }),
    }),
    updateExistingDefence: builder.mutation({
      query: ({ existingDefenceId, updates }) => ({
        url: `/occurrences-data/${existingDefenceId}/update-existing-defence`,
        method: 'PATCH',
        body: updates,
      }),
    }),
    updateEvidence: builder.mutation({
      query: ({ evidenceId, updates }) => ({
        url: `/occurrences-data/${evidenceId}/update-evidence-of-existing-control`,
        method: 'PATCH',
        body: updates,
      }),
    }),
    updateConsequences: builder.mutation({
      query: ({ consequenceId, updates }) => ({
        url: `/occurrences-data/${consequenceId}/update-description-of-consequences`,
        method: 'PATCH',
        body: updates,
      }),
    }),
    updateAssociatedRisks: builder.mutation({
      query: ({ effectId, updates }) => ({
        url: `/occurrences-data/${effectId}/update-associated-risks`,
        method: 'PATCH',
        body: updates,
      }),
    }),
    updatePerformanceTargets: builder.mutation({
      query: ({ targetId, updates }) => ({
        url: `/occurrences-data/${targetId}/update-performance-targets`,
        method: 'PATCH',
        body: updates,
      }),
    }),
    updateMonitoringActivities: builder.mutation({
      query: ({ activityId, updates }) => ({
        url: `/occurrences-data/${activityId}/update-monitoring-activities`,
        method: 'PATCH',
        body: updates,
      }),
    }),
    forgotPassword: builder.mutation({
      query: (emailObj) => ({
        url: '/user/forgotPassword',
        method: 'POST',
        body: emailObj,
      }),
    }),
    setNewPassword: builder.mutation({
      query: (resetObj) => ({
        url: '/user/setNewPassword',
        method: 'POST',
        body: resetObj,
      }),
    }),
  }),
});

export const {
  useGetLessonsQuery,
  useGetLessonByIdQuery,
  useGetPublicationsQuery,
  useGetSafetyLogsQuery,
  useGetOccurrencesQuery,
  useGetOccurrenceByIdQuery,
  useGetIncidentSubCausesQuery,
  useGetArticlesQuery,
  useGetArticleQuery,
  useGetPublicationQuery,
  useGetRecipientsQuery,
  useGetNewsLetterQuery,
  useGetAllNewsletterSubcribersQuery,
  useGetNewsletterdetailsQuery,
  useAddNewsLetterEmailsMutation,
  useSendNewsletterEmailMutation,
  useAddOccurrenceMutation,
  useLoginUserMutation,
  useAddNewsletterSubscriberMutation,
  useUnsubscribeMutation,
  useDeleteArticleMutation,
  useDeletePublicationMutation,
  useDeleteNewsletterMutation,
  useEditArticleMutation,
  useEditPublicationMutation,
  useEditNewsletterMutation,
  useCreateLessonMutation,
  useDeleteLessonMutation,
  useEditLessonMutation,
  useAddActionsTakenMutation,
  useUpdatePreliminaryActionsMutation,
  useAddIncidentCausesMutation,
  useUpdateIncidentCauseMutation,
  useAddSafetyRequirementsMutation,
  useUpdateSafetyRequirementsMutation,
  useUpdateSystemStateMutation,
  useUpdateExistingDefenceMutation,
  useUpdateEvidenceMutation,
  useUpdateConsequencesMutation,
  useUpdateAssociatedRisksMutation,
  useUpdatePerformanceTargetsMutation,
  useUpdateMonitoringActivitiesMutation,
  useGetUsersQuery,
  useGetUserQuery,
  useDeleteUserMutation,
  useSubmitUserMutation,
  useUploadFileMutation,
  useCreateArticleMutation,
  useCreatePublicationMutation,
  useCreateNewsLetterMutation,
  useForgotPasswordMutation,
  useSetNewPasswordMutation,
} = apiSlice;
